import { Box, CircularProgress, Typography } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import { uploadEquipmentImage } from "../../../api/warehouse/equipmentImage";
import { useWarehouseEquipment } from "../../../hooks/Warehouse";
import { EquipmentFormType } from "../../../types/forms/warehouse/equipment";
import WarehouseEquipmentTable from "../Equipment/EquipmentTable";

interface EquipmentTabProps {
  handleError: (error: unknown) => void;
  canEdit: boolean;
}

const EquipmentTab: React.FC<EquipmentTabProps> = ({ handleError, canEdit }) => {
  const { t } = useTranslation();
  const { equipmentData, isPending, isError, error, createEquipment, updateEquipment, toggleEquipmentStatus, refetch } =
    useWarehouseEquipment();

  const handleCreate = async (values: EquipmentFormType, images: File[]) => {
    let equipmentId: number;

    try {
      const created = await createEquipment(values);
      equipmentId = created.id;
    } catch (err) {
      handleError(err);
      throw err;
    }

    // Upload failures don't block the creation
    for (const file of images) {
      try {
        await uploadEquipmentImage(equipmentId, file);
      } catch (err) {
        handleError(err);
      }
    }
  };

  const handleUpdate = async (id: number, values: EquipmentFormType) => {
    try {
      await updateEquipment({ equipmentId: id, values });
    } catch (err) {
      handleError(err);
      throw err;
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await toggleEquipmentStatus(id);
    } catch (err) {
      handleError(err);
      throw err;
    }
  };

  if (isPending && equipmentData.length === 0) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box sx={{ p: 2, textAlign: "center", color: "error.main" }}>
        <Typography variant="h6">
          {t("generic.error-loading-data")}: {error?.message}
        </Typography>
      </Box>
    );
  }

  return (
    <WarehouseEquipmentTable
      equipmentData={equipmentData}
      canEdit={canEdit}
      onCreate={handleCreate}
      onUpdate={handleUpdate}
      onDelete={handleDelete}
      onRequestRefresh={refetch}
    />
  );
};

export default EquipmentTab;